import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { OtsAircraft } from "../../Models/OtsAircraft";
import * as SubActionConstant from "../../constants/SubActionConstant";

interface OpenEventsListState {
  openEventsList: OtsAircraft[];
  lastRefreshed: string;
  openEventsMasterList: OtsAircraft[]; //Creating a master list to store the original list of open events- will be used to filter the list of open events so that API calls are not needed
}

const initialState: OpenEventsListState = {
    openEventsList: {} as OtsAircraft[],
    lastRefreshed: "",
    openEventsMasterList: {} as OtsAircraft[]
};

export const OpenEventsListSlice = createSlice({
  name: "openEventsList",
  initialState,
  reducers: {
    openEvents: (state, action: PayloadAction<OtsAircraft[]>) => {
      // don't override the show flight schedule value 
      if (state.openEventsList.length > 0) {
        let active = state.openEventsList.find(event => event.ShowFlightSchedule === true);
        state.openEventsList = action.payload;
        if (active) { 
          let index = state.openEventsList.findIndex(event => event.id === active.id);
          if (index !== -1) {
            let openEvent = state.openEventsList[index]; 
            openEvent.ShowFlightSchedule = true;
            state.openEventsList[index] = openEvent;
          }
        }
      } else {
        state.openEventsList = action.payload;
      }
    },
    addEvent: (state, action: PayloadAction<OtsEvent>) => {
        
        if (action.payload.tail.startsWith("N")) {
            action.payload.tail = action.payload.tail.slice(1);
        }

        let ots: OtsAircraft = {
            EventType: action.payload.eventType,
            EventTypeCategory: action.payload.eventTypeCategory,
            Station: action.payload.station,
            Location: action.payload.location,
            ProblemText: action.payload.problemText,
            OutOfServiceDateTimeUTC: action.payload.outOfServiceTime ?? "",
            AdviseTimeUTC: action.payload.adviseTime ?? "",
            EstimatedReturnToServiceTimeUTC: action.payload.etrTime ?? null,
            ReturnToServiceTimeUTC: null,
            ATACode: action.payload.ATACode,
            id: "",
            ADVBump: 0,
            ETRBump: 0,
            AtaCodeDescription: action.payload.ataCodeDescription, 
            AircraftRegistration: action.payload.tail, 
            AdviseTimeChanges: [],
            EstimatedReturnToServiceTimeChanges: [],
            Airline: action.payload.airline,
            AssignedTo: "", 
            CreatedBy: "",
            Audit: "",
            IsEtopsCapable: action.payload.isEtopsCapable,
            LastUpdatedBy: action.payload.lastUpdatedBy,
            LastUpdatedTimeStampUTC: "",
            DataClassification: "",
            SpecialEvent: null,
            OTSDays: 0,
            OTSHours: 0,
            LastFlightDate: "",
            OutOfServiceDateTimeChanges: [],
            ProblemTextChanges: [],
            ReturnToServiceTimeChanges: [],
            ConversationUniqueKey: "",
            CreatedDateTimeUTC: new Date()
        }
        state.openEventsList.push(ots);
        state.openEventsMasterList.push(ots);
    },
    returnToService: (state, action: PayloadAction<string>) => {
      const index = state.openEventsList.findIndex(event => event.id === action.payload);
      if (index !== -1) {
        state.openEventsList.splice(index, 1);
      }
      const indexMasterList = state.openEventsMasterList.findIndex(event => event.id === action.payload);
      if (indexMasterList !== -1) {
        state.openEventsMasterList.splice(indexMasterList, 1);
      }
    },
    updateEvent: (state, action: PayloadAction<UpdatedOtsEvent>) => {
      const index = state.openEventsList.findIndex(event => event.id === action.payload.id);
      if (index !== -1) {
        let ots = state.openEventsList[index];
        ots.EventType = action.payload.eventType;
        ots.EventTypeCategory = action.payload.eventTypeCategory;
        ots.EventTypeTo = action.payload.eventTypeTo;
        ots.EventTypeCategoryTo = action.payload.eventTypeCategoryTo;
        ots.Station = action.payload.station;
        ots.Location = action.payload.location;
        ots.ProblemText = action.payload.problemText;
        ots.OutOfServiceDateTimeUTC = action.payload.outOfServiceDateTimeUTC ?? "";
        ots.AdviseTimeUTC = action.payload.adviseTimeUTC ?? "";
        ots.EstimatedReturnToServiceTimeUTC = action.payload.estimatedReturnToServiceTimeUTC ?? "";
        ots.ReturnToServiceTimeUTC = action.payload.returnToServiceTimeUTC ?? "";
        ots.ATACode = action.payload.ATACode; 
        ots.AtaCodeDescription = action.payload.ataCodeDescription, 
        ots.HasPublicAttachment = action.payload.hasPublicAttachment;
        if(action.payload.subAction.includes(SubActionConstant.ETR) && action.payload.estimatedReturnToServiceTimeUTC && !action.payload.adviseTimeUTC){
          ots.ETRBump++;
        }
        if(action.payload.subAction.includes(SubActionConstant.ADV) && action.payload.adviseTimeUTC && !action.payload.estimatedReturnToServiceTimeUTC){
          ots.ADVBump++;
        }
        ots.LastUpdatedBy = action.payload.lastUpdatedBy || "";
        //For special event
        if(action.payload.subAction.includes(SubActionConstant.SPECIAL_EVENT)){
          let newSpecialEvent: SpecialEventHistory | undefined;
          if(action.payload.specialEvent){
            newSpecialEvent = {
              SpecialEventType: action.payload.specialEvent.SpecialEventType,
              StationTo: action.payload.specialEvent.SpecialEventStationTo,
              StationFrom: action.payload.specialEvent.SpecialEventStationFrom,
              ChangedBy: "",
              UpdatedDateTimeUTC: ""
            }
          }
          if(newSpecialEvent){
            if (!Array.isArray(ots.SpecialEventHistory)) {
              ots.SpecialEventHistory = [];
            }
            else{
              ots.SpecialEventHistory = [...ots.SpecialEventHistory];
            }
            ots.SpecialEventHistory.push(newSpecialEvent);
          }
        }
        state.openEventsList[index] = ots;
      }
      const indexMasterList = state.openEventsMasterList.findIndex(event => event.id === action.payload.id);
      if (indexMasterList !== -1) {
        state.openEventsMasterList[indexMasterList] = state.openEventsList[index];
      }
    },
    showFlightSchedule: (state, action: PayloadAction<string>) => {
      const index = state.openEventsList.findIndex(event => event.AircraftRegistration === action.payload);
      if (index !== -1) {
        state.openEventsList[index].ShowFlightSchedule = !state.openEventsList[index].ShowFlightSchedule;
      }
    },
    refreshEvent: (state, action: PayloadAction<OtsAircraft>) => {
      const index = state.openEventsList.findIndex(event => event.id === action.payload.id);
      if (index !== -1) {
        state.openEventsList[index] = action.payload;
      }
      const indexMasterList = state.openEventsMasterList.findIndex(event => event.id === action.payload.id);
      if (indexMasterList !== -1) {
        state.openEventsMasterList[indexMasterList] = state.openEventsList[index];
      }
    },
    setLastRefreshed: (state, action: PayloadAction<string>) => {
      state.lastRefreshed = action.payload;
    }, 
    reopenEvent: (state, action: PayloadAction<OtsAircraft>) => {
      action.payload.ReturnToServiceTimeUTC = null;
      const index = state.openEventsList.findIndex(event => event.id === action.payload.id);
      if (index == -1) {
        state.openEventsList.push(action.payload);
      }
      const indexMasterList = state.openEventsMasterList.findIndex(event => event.id === action.payload.id);
      if (indexMasterList == -1) {
        state.openEventsMasterList.push(action.payload);
      }
    },
    addOpenEventsMasterList: (state, action: PayloadAction<OtsAircraft[]>) => {
      state.openEventsMasterList = action.payload;
    }, 
}});

export default OpenEventsListSlice;
export const { openEvents, addEvent, updateEvent, 
  returnToService, refreshEvent, setLastRefreshed, showFlightSchedule, reopenEvent, addOpenEventsMasterList } = OpenEventsListSlice.actions;